import React from 'react';
import { Box, Fade, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import GlobalAppTechnicalSupportAI from './GlobalAppTechnicalSupportAI';
import {
  closeGlobalAppHelpFloating,
  useGlobalAppHelpFloating,
} from 'hooks/useGlobalAppHelpFloating';
import {
  GLOBAL_APP_HELP_PANEL_MAX_HEIGHT_PX,
  GLOBAL_APP_HELP_PANEL_WIDTH_PX,
} from 'layout/MainLayout/layout-zoom-constants';

/** ווידג'ט צף — עוזר תמיכה AI (סגנון Enterprise: Intercom / HubSpot) */
const GlobalAppHelpFloatingWidget: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { isOpen } = useGlobalAppHelpFloating();

  const panelWidth = isMobile ? 'calc(100vw - 16px)' : GLOBAL_APP_HELP_PANEL_WIDTH_PX;
  const panelHeight = isMobile
    ? 'min(92dvh, 680px)'
    : isTablet
      ? `min(${GLOBAL_APP_HELP_PANEL_MAX_HEIGHT_PX}px, calc(100dvh - 56px))`
      : `min(${GLOBAL_APP_HELP_PANEL_MAX_HEIGHT_PX}px, calc(100dvh - 48px))`;

  return (
    <>
      <Fade in={isOpen} timeout={240} unmountOnExit>
        <Box
          role="presentation"
          aria-hidden={!isOpen}
          onClick={closeGlobalAppHelpFloating}
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: theme.zIndex.snackbar,
            bgcolor: alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.5 : 0.32),
            backdropFilter: 'blur(3px)',
            WebkitBackdropFilter: 'blur(3px)',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }}
        />
      </Fade>
      <Fade in={isOpen} timeout={300} unmountOnExit>
        <Box
          role="dialog"
          aria-modal="true"
          aria-label="Technical support"
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: 'fixed',
            zIndex: theme.zIndex.snackbar + 1,
            bottom: { xs: 8, sm: 24 },
            insetInlineEnd: { xs: 8, sm: 24 },
            insetInlineStart: 'auto',
            width: panelWidth,
            maxWidth: 'calc(100vw - 16px)',
            height: panelHeight,
            pb: 'env(safe-area-inset-bottom, 0px)',
            borderRadius: { xs: '16px', sm: '20px' },
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: theme.palette.background.paper,
            border: '1px solid',
            borderColor: alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.4 : 0.65),
            boxShadow:
              theme.palette.mode === 'dark'
                ? `0 32px 80px ${alpha(theme.palette.common.black, 0.6)}, 0 8px 24px ${alpha(theme.palette.common.black, 0.35)}`
                : `0 32px 72px ${alpha(theme.palette.common.black, 0.18)}, 0 12px 32px ${alpha(theme.palette.common.black, 0.08)}`,
            transformOrigin: theme.direction === 'rtl' ? 'bottom left' : 'bottom right',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }}
        >
          <GlobalAppTechnicalSupportAI />
        </Box>
      </Fade>
    </>
  );
};

export default GlobalAppHelpFloatingWidget;
